import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Clock3,
  BadgeCheck,
  IndianRupee,
  MapPin,
  FolderOpen,
  Stethoscope,
  Pencil,
  CheckCircle2,
  Circle,
  Lock,
} from "lucide-react";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { clearRequestDraft, readRequestDraft } from "./requestDraftSession";
import useSocketLiveRefresh from "../../hooks/useSocketLiveRefresh";
import "./RequestFlow.scss";
import WizardSteps from "./WizardSteps";

function formatCategory(req) {
  const s = String(req?.categoryLabel || "").trim();
  if (s) return s;
  const blocks = Array.isArray(req?.selectionsExpanded) ? req.selectionsExpanded : [];
  const parts = [];
  for (const block of blocks) {
    const cat = block?.categoryName || "";
    const subs = (block?.subCategories || []).map((x) => x?.subCategoryName).filter(Boolean);
    if (cat && subs.length) parts.push(`${cat} · ${subs.join(", ")}`);
    else if (cat) parts.push(cat);
  }
  return parts.join(" · ") || "—";
}

/**
 * After category submit: optional symptoms/insurance edits (PATCH), then open bidding.
 */
export default function RequestReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [symptoms, setSymptoms] = useState("");
  const [insurance, setInsurance] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitOutcome, setSubmitOutcome] = useState("");
  const draft = useMemo(() => readRequestDraft(id), [id]);
  const linkedDocCount = useMemo(
    () => (Array.isArray(data?.requestDocuments) ? data.requestDocuments.length : 0),
    [data?.requestDocuments]
  );

  const load = useCallback(async (opts) => {
    const silent = Boolean(opts?.silent);
    if (!id) return;
    if (!silent) {
      setLoading(true);
      setErr("");
    }
    try {
      const res = await apiRequest({ method: "GET", url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(id) });
      const row = res?.request || null;
      setData(row);
      if (row) {
        setSymptoms(String(row.symptomsDescription || ""));
        setInsurance(String(row.insuranceProviderName || ""));
      } else if (!silent) {
        setData(null);
      }
    } catch (e) {
      if (!silent) {
        setErr(e?.message || "Failed to load request");
        setData(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const waitingAdminReview =
    Boolean(id) &&
    !loading &&
    Boolean(data?.submittedForReviewAt) &&
    String(data?.requestWorkflowStatus || "").toUpperCase() === "PENDING_ADMIN_REVIEW";

  useSocketLiveRefresh({
    enabled: waitingAdminReview,
    role: "patient",
    events: ["me:refresh", "notification:new", "bid:update"],
    onEvent: (payload) => {
      const t = String(payload?.type || "").toLowerCase();
      if (t === "chat_message" || t === "chat_reply") return;
      const mr = String(payload?.matchRequestId || "").trim();
      if (mr && id && mr !== String(id)) return;
      void load({ silent: true });
    },
    pollMs: 8000,
  });

  /** Catch up when the tab becomes visible again; live path is Socket.IO. */
  useEffect(() => {
    if (!waitingAdminReview) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") void load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [waitingAdminReview, load]);

  useEffect(() => {
    const wf = String(data?.requestWorkflowStatus || "").toUpperCase();
    if (wf === "VERIFIED" || wf === "BIDDING" || wf === "BID_SELECTED" || wf === "BOOKED" || wf === "VISIT_COMPLETED") {
      setSubmitOutcome("");
    }
  }, [data?.requestWorkflowStatus]);

  const goToCarePrice = useCallback(() => {
    try {
      window.localStorage.setItem("healbid:careprice:lastMatchRequestId", id);
    } catch (_) {}
    navigate("/care-price", { replace: true, state: { matchRequestId: id } });
  }, [id, navigate]);

  const goToDoctorReplies = useCallback(() => {
    navigate("/patient-chats", { replace: true });
  }, [navigate]);

  const onSubmitForVerification = async () => {
    if (!id) return;
    if (
      Boolean(data?.submittedForReviewAt) ||
      ["PENDING_ADMIN_REVIEW", "VERIFIED", "BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(
        String(data?.requestWorkflowStatus || "").toUpperCase()
      )
    ) {
      return;
    }
    if (!linkedDocCount) {
      setErr("Add at least one document before submitting for review.");
      navigate(`/request/${id}/upload`);
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      if (String(id).startsWith("local-")) {
        clearRequestDraft(id);
        navigate("/patient-requests", { replace: true });
        return;
      }
      const categoryId = draft?.selectedIds?.[0];
      const selectedSubCategoryIds = Array.isArray(draft?.selectedIds)
        ? draft.selectedIds.slice(1).filter(Boolean)
        : [];
      if (!categoryId || selectedSubCategoryIds.length === 0) {
        setErr(
          "Your procedure choice was not saved in this browser (or it is out of date). Open the category step again, pick the full path down to the last list item, then return here."
        );
        navigate(`/request/${id}/category`);
        return;
      }
      const budget = String(draft?.budget || draft?.affordabilityPrice || "").trim();
      if (!budget || !Number.isFinite(Number(budget)) || Number(budget) <= 0) {
        setErr("Set your location and budget before submitting.");
        navigate(`/request/${id}/budget`);
        return;
      }
      const locationScopeUi = String(draft?.locationScopeUi || "withinZone");
      const apiScope =
        draft?.locationScope ||
        (locationScopeUi === "pincode"
          ? "locationWise"
          : locationScopeUi === "westBengal"
            ? "allOverWestBengal"
            : "withinZone");
      const packageScope =
        draft?.packageScope && typeof draft.packageScope === "object"
          ? draft.packageScope
          : { procedureOt: true, implants: false, hospitalStay: false };

      const submitRes = await apiRequest({
        method: "POST",
        url: ENDPOINTS.SUBMIT_MATCH_REQUEST(String(id)),
        data: {
          selections: [
            {
              categoryId,
              // Persist each tier you selected (L2.. up to L5); terminal must be a leaf (server-validated).
              subCategoryIds: selectedSubCategoryIds,
            },
          ],
          affordabilityPrice: budget,
          maxBudget: budget,
          locationScope: apiScope,
          packageScope,
          ...(apiScope === "withinZone"
            ? {
                patientLat: draft?.patientLat,
                patientLng: draft?.patientLng,
                withinZoneRadiusKm: draft?.withinZoneRadiusKm || 20,
              }
            : {}),
          ...(apiScope === "locationWise" ? { searchPincode: String(draft?.searchPincode || "").trim() } : {}),
        },
      });
      await apiRequest({
        method: "PATCH",
        url: ENDPOINTS.UPDATE_MATCH_REQUEST(String(id)),
        data: {
          symptomsDescription: String(draft?.description || symptoms).trim(),
          insuranceProviderName: String(draft?.insurance || insurance).trim(),
          affordabilityPrice: budget,
          maxBudget: budget,
          locationScope: apiScope,
          packageScope,
          ...(apiScope === "withinZone"
            ? {
                patientLat: draft?.patientLat,
                patientLng: draft?.patientLng,
                withinZoneRadiusKm: draft?.withinZoneRadiusKm || 20,
              }
            : {}),
          ...(apiScope === "locationWise" ? { searchPincode: String(draft?.searchPincode || "").trim() } : {}),
        },
      }).catch(() => {});
      clearRequestDraft(id);
      await load();
      const workflowStatus = String(submitRes?.requestWorkflowStatus || "").toUpperCase();
      if (workflowStatus === "BIDDING") {
        goToDoctorReplies();
        return;
      }
      if (workflowStatus === "PENDING_ADMIN_REVIEW") {
        setSubmitOutcome(
          "Submitted. We’re checking your documents — usually within 2–4 hours. Your budget is saved. We’ll notify you when you can confirm and notify matching doctors."
        );
      } else {
        setSubmitOutcome(
          "Submitted. We’re checking your documents — usually within 2–4 hours. Your budget is saved. We’ll notify you when you can confirm and notify matching doctors."
        );
      }
    } catch (e) {
      setErr(e?.message || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  const w = data?.requestWorkflowStatus || "—";
  const ver = data?.documentVerification;
  const overall = String(ver?.overallStatus || "").toLowerCase();
  const rejectedDocs =
    data?.requestDocuments?.filter((d) => String(d?.status).toLowerCase() === "rejected") || [];
  const docCount = linkedDocCount;
  const workflow = String(w || "").toUpperCase();
  const isSubmittedForVerification =
    Boolean(data?.submittedForReviewAt) ||
    ["PENDING_ADMIN_REVIEW", "VERIFIED", "BIDDING", "BID_SELECTED", "BOOKED", "VISIT_COMPLETED"].includes(workflow);
  const isAwaitingAdminApproval = workflow === "PENDING_ADMIN_REVIEW";
  const isReadyToBid = workflow === "VERIFIED";
  const canEdit = !isSubmittedForVerification;

  const procedureNames = Array.isArray(draft?.selectedNames) ? draft.selectedNames.filter(Boolean) : [];
  const procedureLabel = procedureNames.length
    ? procedureNames
    : formatCategory(data) !== "—"
      ? [formatCategory(data)]
      : [];
  const budgetRaw = String(draft?.budget || draft?.affordabilityPrice || data?.affordabilityPrice || "").trim();
  const budgetNum = Number(budgetRaw);
  const budgetLabel =
    budgetRaw && Number.isFinite(budgetNum) && budgetNum > 0
      ? `₹${Math.round(budgetNum).toLocaleString("en-IN")}`
      : "";
  const descriptionText = String(draft?.description || symptoms || "").trim();
  const insuranceText = String(draft?.insurance || insurance || "").trim();
  const packageScope =
    draft?.packageScope && typeof draft.packageScope === "object" ? draft.packageScope : null;
  const packageBits = [];
  if (packageScope) {
    if (packageScope.procedureOt !== false) packageBits.push("OT / procedure");
    if (packageScope.implants) packageBits.push("Implants");
    if (packageScope.hospitalStay) packageBits.push("Hospital stay");
  }
  const locationLabel = (() => {
    const ui = String(draft?.locationScopeUi || "");
    if (ui === "westBengal") return "All over West Bengal";
    if (ui === "pincode") {
      const pin = String(draft?.searchPincode || "").trim();
      return pin ? `Pincode ${pin}` : "By pincode";
    }
    if (ui === "withinZone") return `Near me · ${draft?.withinZoneRadiusKm || 20} km`;
    const scope = String(data?.locationScope || "");
    if (scope === "allOverWestBengal") return "All over West Bengal";
    if (scope === "locationWise") return `Pincode ${data?.searchPincode || ""}`.trim();
    if (scope === "withinZone") return "Near me";
    return "";
  })();
  const readyChecks = [
    { label: "Procedure", done: procedureLabel.length > 0 },
    { label: "Budget", done: Boolean(budgetLabel) },
    { label: "Location", done: Boolean(locationLabel) },
    { label: "Documents", done: docCount > 0 },
  ];
  const ready = readyChecks.every((c) => c.done);

  if (!id) return null;
  if (loading) {
    return (
      <div className="reqReview reqReview--surface">
        <p className="reqReview__p">Loading…</p>
      </div>
    );
  }
  if (err && !data) {
    return (
      <div className="reqReview reqReview--surface">
        <p className="reqReview__p">{err}</p>
        <button type="button" className="reqReview__btn" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="reqWizard">
      <div className="reqWizard__card">
        <button
          type="button"
          className="reqWizard__back reqWizard__back--dupGlobalNav"
          onClick={() => navigate(`/request/${id}/details`)}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <p className="reqWizard__eyebrow">New care request</p>
        <h1 className="reqWizard__title">Review &amp; submit</h1>
        <p className="reqWizard__subtitle">
          One last look — then we send it for a quick admin check. Doctors see your budget only after you notify them.
        </p>
        <WizardSteps active={4} />

        <div className="reqReviewSummary">
          {canEdit ? (
            <div className={`reqReviewReady ${ready ? "is-ready" : ""}`}>
              <div className="reqReviewReady__head">
                <span className="reqReviewReady__icon" aria-hidden>
                  {ready ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                </span>
                <div>
                  <p className="reqReviewReady__title">{ready ? "Ready to submit" : "Almost ready"}</p>
                  <p className="reqReviewReady__body">
                    {ready
                      ? "Everything looks set. Submit for admin review when you’re happy."
                      : "Fill the missing pieces below, then submit."}
                  </p>
                </div>
              </div>
              <div className="reqReviewReady__chips">
                {readyChecks.map((c) => (
                  <span key={c.label} className={`reqReviewChip ${c.done ? "is-done" : ""}`}>
                    {c.done ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className={`reqReviewBudget ${budgetLabel ? "" : "is-missing"}`}>
            <div className="reqReviewBudget__top">
              <span className="reqReviewBudget__icon" aria-hidden>
                <IndianRupee size={18} />
              </span>
              <p className="reqReviewBudget__label">Draft surgery budget</p>
              {canEdit ? (
                <button type="button" className="reqReviewEdit" onClick={() => navigate(`/request/${id}/budget`)}>
                  Edit
                </button>
              ) : null}
            </div>
            <p className="reqReviewBudget__amount">{budgetLabel || "Not set"}</p>
            <p className="reqReviewBudget__hint">
              {budgetLabel
                ? "Doctors see this only after you notify them"
                : "Add a draft amount before submitting"}
            </p>
            {packageBits.length ? (
              <div className="reqReviewBudget__tags">
                {packageBits.map((bit) => (
                  <span key={bit}>{bit}</span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="reqReviewMetrics">
            <button
              type="button"
              className="reqReviewMetric"
              disabled={!canEdit}
              onClick={() => canEdit && navigate(`/request/${id}/budget`)}
            >
              <span className="reqReviewMetric__icon" aria-hidden>
                <MapPin size={16} />
              </span>
              {canEdit ? <Pencil size={13} className="reqReviewMetric__edit" aria-hidden /> : null}
              <span className="reqReviewMetric__label">Location</span>
              <span className={`reqReviewMetric__value ${locationLabel ? "" : "is-muted"}`}>
                {locationLabel || "Not set"}
              </span>
            </button>
            <button
              type="button"
              className="reqReviewMetric"
              disabled={!canEdit}
              onClick={() => canEdit && navigate(`/request/${id}/upload`)}
            >
              <span className="reqReviewMetric__icon" aria-hidden>
                <FolderOpen size={16} />
              </span>
              {canEdit ? <Pencil size={13} className="reqReviewMetric__edit" aria-hidden /> : null}
              <span className="reqReviewMetric__label">Documents</span>
              <span className={`reqReviewMetric__value ${docCount ? "" : "is-muted"}`}>
                {docCount ? `${docCount} file${docCount === 1 ? "" : "s"}` : "None linked"}
              </span>
            </button>
          </div>

          <section className="reqReviewPanel">
            <div className="reqReviewPanel__head">
              <span className="reqReviewPanel__icon" aria-hidden>
                <Stethoscope size={16} />
              </span>
              <h2>Procedure</h2>
              {canEdit ? (
                <button type="button" className="reqReviewEdit" onClick={() => navigate(`/request/${id}/category`)}>
                  Edit
                </button>
              ) : null}
            </div>
            {procedureLabel.length ? (
              <div className="reqReviewPath">
                {procedureLabel.map((name, i) => (
                  <span key={`${name}-${i}`} className="reqReviewPath__item">
                    {i > 0 ? <ChevronRight size={14} className="reqReviewPath__sep" aria-hidden /> : null}
                    <span className={i === procedureLabel.length - 1 ? "is-leaf" : ""}>{name}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="reqReviewPanel__empty">No procedure selected</p>
            )}

            <div className="reqReviewDetail">
              <div>
                <p className="reqReviewDetail__label">Description</p>
                <p className="reqReviewDetail__value">{descriptionText || "—"}</p>
              </div>
              {canEdit ? (
                <button type="button" className="reqReviewIconBtn" onClick={() => navigate(`/request/${id}/details`)}>
                  <Pencil size={14} />
                </button>
              ) : null}
            </div>
            <div className="reqReviewDetail">
              <div>
                <p className="reqReviewDetail__label">Insurance</p>
                <p className={`reqReviewDetail__value ${insuranceText ? "" : "is-muted"}`}>
                  {insuranceText || "Not added"}
                </p>
              </div>
              {canEdit ? (
                <button type="button" className="reqReviewIconBtn" onClick={() => navigate(`/request/${id}/details`)}>
                  <Pencil size={14} />
                </button>
              ) : null}
            </div>
          </section>

          {canEdit ? (
            <div className="reqReviewLockNote">
              <Lock size={16} aria-hidden />
              <p>
                Submitting starts an admin check only. Matching doctors are not notified until you confirm later.
              </p>
            </div>
          ) : null}
        </div>

      {overall === "rejected" && rejectedDocs.length > 0 ? (
        <ul className="reqReview__list">
          {rejectedDocs.map((d) => (
            <li key={d.documentId}>
              <strong>{d.originalName || "Document"}</strong>
              {d.rejectionReason ? ` — ${d.rejectionReason}` : ""}
            </li>
          ))}
        </ul>
      ) : null}

      {err ? <p className="reqReview__p reqReview__p--err">{err}</p> : null}
      {submitOutcome && !isAwaitingAdminApproval ? (
        <p className="reqReview__p">{submitOutcome}</p>
      ) : null}
      {isAwaitingAdminApproval || (isSubmittedForVerification && !isReadyToBid) ? (
        <div className="reqMilestone" role="status">
          <div className="reqMilestone__head">
            <span className="reqMilestone__icon" aria-hidden>
              <Clock3 size={18} strokeWidth={2.25} />
            </span>
            <div>
              <p className="reqMilestone__title">Request submitted</p>
              <p className="reqMilestone__body">
                We’re checking your documents — usually within 2–4 hours. Your budget is saved.
              </p>
            </div>
          </div>
          <ol className="reqMilestone__steps">
            <li className="is-done">Docs in review</li>
            <li>Notify doctors</li>
            <li>Get replies</li>
          </ol>
        </div>
      ) : null}
      {isReadyToBid ? (
        <div className="reqMilestone reqMilestone--go" role="status">
          <div className="reqMilestone__head">
            <span className="reqMilestone__icon reqMilestone__icon--ok" aria-hidden>
              <BadgeCheck size={18} strokeWidth={2.25} />
            </span>
            <div>
              <p className="reqMilestone__title">Documents approved</p>
              <p className="reqMilestone__body">
                You’re cleared to notify matching doctors with your draft budget. They only see it after you tap Notify.
              </p>
            </div>
          </div>
          <ol className="reqMilestone__steps">
            <li className="is-done">Docs approved</li>
            <li className="is-current">Notify doctors</li>
            <li>Get replies</li>
          </ol>
        </div>
      ) : null}
        <div className="reqWizard__actions">
          <button
            type="button"
            className="reqWizard__ghost reqWizard__ghost--mobileHidden reqWizard__ghost--dupWithCardBack"
            onClick={() => navigate(`/request/${id}/details`)}
          >
            Back
          </button>
          {isReadyToBid ? (
            <button type="button" className="reqWizard__primary" onClick={() => goToCarePrice()}>
              Notify doctors
              <ChevronRight size={14} />
            </button>
          ) : isAwaitingAdminApproval || isSubmittedForVerification ? (
            <button
              type="button"
              className="reqWizard__primary"
              onClick={() => navigate("/HealBidDashboard", { replace: true })}
            >
              Back to home
            </button>
          ) : (
            <button type="button" className="reqWizard__primary" disabled={submitting} onClick={() => void onSubmitForVerification()}>
              {submitting ? "Submitting..." : "Submit request"}
              <ChevronRight size={14} />
            </button>
          )}
        </div>

        {w === "BIDDING" || w === "BID_SELECTED" || w === "BOOKED" || w === "VISIT_COMPLETED" ? (
          <div className="reqReview__btns">
            <button
              type="button"
              className="reqReview__btn"
              onClick={() => {
                if (w === "BOOKED" || w === "VISIT_COMPLETED") {
                  navigate("/appointment", { replace: true });
                  return;
                }
                goToDoctorReplies();
              }}
            >
              {w === "BOOKED" || w === "VISIT_COMPLETED" ? "View appointment" : "Open messages"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

